import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { countryForNotbankCurrency } from '../add-cashout/currencies/notbank-cashout.logic';

export const NOTBANK_GENDER_OPTIONS = [
  { value: 1, label: 'Hombre (1)' },
  { value: 0, label: 'Mujer (0)' },
];

export const NOTBANK_IDENTITY_TYPE_OPTIONS = [
  { value: '1', label: 'DNI / cédula (1)' },
  { value: '8', label: 'Pasaporte (8)' },
  { value: '2', label: 'Otro (2)' },
];

export const NOTBANK_CIVIL_STATUS_OPTIONS = [
  { value: 'single', label: 'Soltero/a' },
  { value: 'married', label: 'Casado/a' },
  { value: 'divorced', label: 'Divorciado/a' },
  { value: 'widowed', label: 'Viudo/a' },
];

/** Base reliance fields always required by Notbank register. */
export const NOTBANK_BASE_REGISTER_FIELDS = [
  'first_name',
  'last_name',
  'phone_number',
  'profession',
  'gender',
  'birthdate',
  'citizenship',
  'identity_type',
  'identity_number',
  'identity_country',
  'address_country',
  'address_city',
  'address_street',
  'address_postal_code',
] as const;

export const NOTBANK_FIELD_LABELS: Record<string, string> = {
  first_name: 'Nombre',
  last_name: 'Apellido',
  phone_number: 'Teléfono (E.164)',
  profession: 'Profesión',
  gender: 'Género',
  birthdate: 'Fecha de nacimiento (DD/MM/YYYY)',
  citizenship: 'Nacionalidad (ISO)',
  identity_type: 'Tipo de documento',
  identity_number: 'Número de documento',
  identity_country: 'País del documento (ISO)',
  address_country: 'País de domicilio (ISO)',
  address_city: 'Ciudad',
  address_street: 'Calle / dirección',
  address_postal_code: 'Código postal',
  address_comune: 'Comuna (Chile)',
  address_district: 'Barrio / distrito (Brasil)',
  address_number: 'Número de puerta (Brasil)',
  address_state: 'Estado (Brasil, ej. SP)',
  address_province: 'Provincia (Argentina)',
  subject_comply: 'Sujeto obligado (ARS)',
  civil_status: 'Estado civil (Perú)',
  pep: 'Es PEP (Perú)',
  pep_position: 'Cargo PEP',
  pep_institution: 'Institución PEP',
};

export function extraRegisterFieldsForCountry(country: string): string[] {
  const co = (country || '').toUpperCase();
  if (co === 'CL') return ['address_comune'];
  if (co === 'BR') return ['address_district', 'address_number', 'address_state'];
  if (co === 'AR') return ['address_province', 'subject_comply'];
  if (co === 'PE') return ['civil_status', 'pep'];
  return [];
}

export function createNotbankRegisterFormGroup(
  fb: FormBuilder,
  currency: string,
  partial?: Record<string, unknown> | null,
): FormGroup {
  const country = countryForNotbankCurrency(currency);
  const p = partial || {};
  const str = (key: string, fallback = '') =>
    p[key] != null && String(p[key]).trim() !== ''
      ? String(p[key])
      : fallback;

  return fb.group({
    first_name: [str('first_name'), Validators.required],
    last_name: [str('last_name'), Validators.required],
    phone_number: [str('phone_number'), Validators.required],
    profession: [str('profession', 'Empleado'), Validators.required],
    gender: [
      p['gender'] != null ? Number(p['gender']) : 1,
      Validators.required,
    ],
    birthdate: [str('birthdate'), Validators.required],
    citizenship: [str('citizenship', country), Validators.required],
    identity_type: [str('identity_type', '1'), Validators.required],
    identity_number: [str('identity_number'), Validators.required],
    identity_country: [str('identity_country', country), Validators.required],
    address_country: [str('address_country', country), Validators.required],
    address_city: [str('address_city'), Validators.required],
    address_street: [str('address_street'), Validators.required],
    address_postal_code: [str('address_postal_code'), Validators.required],
    address_comune: [str('address_comune')],
    address_district: [str('address_district')],
    address_number: [str('address_number')],
    address_state: [str('address_state')],
    address_province: [str('address_province')],
    subject_comply: [p['subject_comply'] === true || p['subject_comply'] === 'true'],
    civil_status: [str('civil_status', 'single')],
    pep: [p['pep'] === true || p['pep'] === 'true'],
    pep_position: [str('pep_position')],
    pep_institution: [str('pep_institution')],
    language: [str('language', 'es')],
  });
}

export function applyCountryRegisterValidators(
  group: FormGroup,
  currency: string,
): void {
  const country = countryForNotbankCurrency(currency);
  const extras = new Set(extraRegisterFieldsForCountry(country));
  const allExtra = [
    'address_comune',
    'address_district',
    'address_number',
    'address_state',
    'address_province',
    'subject_comply',
    'civil_status',
    'pep',
    'pep_position',
    'pep_institution',
  ];
  for (const name of allExtra) {
    const c = group.get(name);
    if (!c) continue;
    if (extras.has(name) || (name.startsWith('pep_') && group.get('pep')?.value === true && country === 'PE')) {
      c.setValidators([Validators.required]);
    } else {
      c.clearValidators();
    }
    c.updateValueAndValidity({ emitEvent: false });
  }
  if (country === 'AR') {
    // subject_comply is boolean; required means must be set (false is ok)
    group.get('subject_comply')?.clearValidators();
    group.get('subject_comply')?.updateValueAndValidity({ emitEvent: false });
  }
}

export function buildRegisterPayloadFromForm(group: FormGroup): Record<string, unknown> {
  const v = group.getRawValue() as Record<string, any>;
  const out: Record<string, unknown> = {
    first_name: String(v['first_name'] || '').trim(),
    last_name: String(v['last_name'] || '').trim(),
    phone_number: String(v['phone_number'] || '').replace(/\s+/g, '').trim(),
    profession: String(v['profession'] || '').trim(),
    gender: Number(v['gender']),
    birthdate: String(v['birthdate'] || '').trim(),
    citizenship: String(v['citizenship'] || '').trim().toUpperCase(),
    identity_type: String(v['identity_type'] || '').trim(),
    identity_number: String(v['identity_number'] || '')
      .replace(/[.\s-]/g, '')
      .trim(),
    identity_country: String(v['identity_country'] || '').trim().toUpperCase(),
    address_country: String(v['address_country'] || '').trim().toUpperCase(),
    address_city: String(v['address_city'] || '').trim(),
    address_street: String(v['address_street'] || '').trim(),
    address_postal_code: String(v['address_postal_code'] || '').trim(),
    language: String(v['language'] || 'es').trim() || 'es',
  };
  for (const key of [
    'address_comune',
    'address_district',
    'address_number',
    'address_state',
    'address_province',
    'civil_status',
    'pep_position',
    'pep_institution',
  ]) {
    const val = String(v[key] || '').trim();
    if (val) out[key] = val;
  }
  if (v['subject_comply'] != null) out['subject_comply'] = !!v['subject_comply'];
  if (v['pep'] != null) out['pep'] = !!v['pep'];
  return out;
}

export function isNotbankAccountProvisioned(resp: any): boolean {
  const data = resp?.data ?? resp;
  if (!data || typeof data !== 'object') return false;
  if (data.ready === true) return true;
  const userId = data.user_id || data.notbank_user_id;
  const accountId = data.account_id || data.notbank_account_id;
  return Boolean(userId && accountId);
}

export function extractNotbankErrorBody(err: any): Record<string, any> {
  // HttpService often rethrows error.error (body only), not HttpErrorResponse.
  if (err?.error != null && typeof err.error === 'object' && !Array.isArray(err.error)) {
    return err.error;
  }
  if (err && typeof err === 'object' && !Array.isArray(err)) {
    return err;
  }
  return { message: String(err ?? '') };
}

export function isNotbankProvisionRequiredError(err: any): boolean {
  const body = extractNotbankErrorBody(err);
  const code = String(body['code'] || '').toLowerCase();
  const message = String(body['message'] || body['detail'] || '').toLowerCase();
  if (
    code === 'local_register_required' ||
    code === 'bank_account_required' ||
    code === 'local_account_required' ||
    // legacy aliases
    code === 'notbank_register_required' ||
    code === 'notbank_bank_account_required' ||
    code === 'notbank_account_required'
  ) {
    return true;
  }
  if (Array.isArray(body['missing_fields']) && body['missing_fields'].length > 0) {
    return true;
  }
  // GET /local-account/account 404 body (HttpService strips HTTP status)
  if (
    message.includes('no local account') ||
    message.includes('no notbank account provisioned') ||
    message.includes('provision first') ||
    message.includes('local register') ||
    message.includes('notbank register')
  ) {
    return true;
  }
  return false;
}

